import { type MouseEvent } from 'react'
import cn from 'classnames'
import type { Course } from '@mc/api'
import { useTranslation, withI18nContext } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import { TrackingContainer } from '@mc/visibility'
import { useIsPen7395HeroOnly } from 'experiments/pen-7395_v2_cm_hero_only'
import { PRO_SECTION_ID } from '../constants'
import { trackProBannerLearnMoreClicked, trackProHeroViewed } from '../events'
import { EXPERIMENT_I18N_NAMESPACE, i18nInstance } from '../i18n'
import { scrollToProSection } from '../scrollToProSection'
import { ProLockup } from './ProLockup'
import classes from './ProBanner.module.scss'

const CARD_CLASS_NAME = cn(
  'd-flex flex-column align-items-start position-relative mc-overflow--hidden',
  classes.card,
)

const ProBannerComp = () => {
  const { t } = useTranslation(EXPERIMENT_I18N_NAMESPACE)
  const isPen7395HeroOnly = useIsPen7395HeroOnly()
  const { course } = useStaticData<{ course: Course }>()
  const slug = course?.slug ?? ''

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    trackProBannerLearnMoreClicked(slug)
    scrollToProSection()
  }

  const content = (
    <>
      <span aria-hidden className={classes.drift} />
      <span aria-hidden className={classes.sweep} />
      <span aria-hidden className={classes.rim} />
      <span className={classes.lead}>
        {t('proBanner.includedIn')}{' '}
        <ProLockup weight='medium' className={classes.lockup} />
      </span>
      <span className={classes.detail}>
        {t('proBanner.body')}
        {!isPen7395HeroOnly && (
          <>
            {' '}
            <span className={classes.learnMore}>
              {t('proBanner.learnMore')}
            </span>
          </>
        )}
      </span>
    </>
  )

  return (
    <TrackingContainer
      onFirstVisible={() => trackProHeroViewed(slug)}
      className={cn('position-relative mc-mt-4', classes.stage)}
    >
      {isPen7395HeroOnly ? (
        <div className={CARD_CLASS_NAME}>{content}</div>
      ) : (
        <a
          href={`#${PRO_SECTION_ID}`}
          onClick={handleClick}
          className={cn(CARD_CLASS_NAME, classes.interactive)}
        >
          {content}
        </a>
      )}
    </TrackingContainer>
  )
}

export const ProBanner = withI18nContext(i18nInstance)(ProBannerComp)
